import { ErreurApi } from '../../api/client.js';
import type { ActionsFiche, VueFiche } from './FicheHeritier.js';
import { MOTIF_PAUSE } from './libelles.js';
import type { DonneesFiche } from './modele.js';
import type { SourceFiche } from './source.js';

export interface EtatEcran {
  statut: 'sans-selection' | 'chargement' | 'pret' | 'erreur';
  donnees: DonneesFiche | null;
  vue: VueFiche;
  message: string | null;
}
export interface ControleurFiche {
  etat: () => EtatEcran;
  abonner: (ecouteur: () => void) => () => void;
  charger: () => Promise<void>;
  actions: ActionsFiche;
}

export const MESSAGE_SANS_SELECTION = 'Ouvrez une fiche héritier depuis le dossier pour la consulter ici.';
const MESSAGE_INCONNU = 'Une erreur est survenue de notre côté. Réessayez dans un instant ; si elle persiste, contactez le Cabinet MAAT.';

const phrase = (erreur: unknown): string => (erreur instanceof ErreurApi ? erreur.messageUtilisateur : MESSAGE_INCONNU);

/** Contrôleur de l'écran D05 : état, chargement et écritures (toujours suivies d'un rechargement de la fiche). */
export function creerControleur(source: SourceFiche, selection: { personId: string; dossierId: string } | null): ControleurFiche {
  let etat: EtatEcran = {
    statut: selection ? 'chargement' : 'sans-selection',
    donnees: null,
    vue: { coordonneesVisibles: false, alerte: null, destinatairesCoches: [], consentement: null, message: null },
    message: selection ? null : MESSAGE_SANS_SELECTION,
  };
  const ecouteurs = new Set<() => void>();
  const changer = (suite: Partial<EtatEcran>, vue: Partial<VueFiche> = {}): void => {
    etat = { ...etat, ...suite, vue: { ...etat.vue, ...vue } };
    for (const e of ecouteurs) e();
  };

  const charger = async (): Promise<void> => {
    if (!selection) {
      changer({ statut: 'sans-selection', message: MESSAGE_SANS_SELECTION });
      return;
    }
    try {
      changer({ statut: 'pret', donnees: await source.charger(selection.personId, selection.dossierId), message: null });
    } catch (erreur) {
      changer({ statut: 'erreur', message: phrase(erreur) });
    }
  };

  /** Une écriture : en cas de succès, la fiche est relue et le résultat annoncé ; sinon la phrase d'erreur s'affiche. */
  const ecrire = async (ecriture: () => Promise<void>, succes: string, aSucces: Partial<VueFiche> = {}): Promise<void> => {
    try {
      await ecriture();
    } catch (erreur) {
      changer({}, { message: phrase(erreur) });
      return;
    }
    await charger();
    changer({}, { ...aSucces, message: succes });
  };

  return {
    etat: () => etat,
    abonner: (ecouteur) => {
      ecouteurs.add(ecouteur);
      return () => {
        ecouteurs.delete(ecouteur);
      };
    },
    charger,
    actions: {
      onAfficherCoordonnees: () => changer({}, { coordonneesVisibles: true }),
      onOuvrirAlerte: (etiquetteId) => changer({}, { alerte: etiquetteId, destinatairesCoches: [], message: null }),
      onFermerAlerte: () => changer({}, { alerte: null, destinatairesCoches: [] }),
      onBasculerDestinataire: (cle) =>
        changer(
          {},
          {
            destinatairesCoches: etat.vue.destinatairesCoches.includes(cle)
              ? etat.vue.destinatairesCoches.filter((c) => c !== cle)
              : [...etat.vue.destinatairesCoches, cle],
          },
        ),
      onConfirmerPartage: (etiquetteId, corps) =>
        corps.recipients.length === 0
          ? Promise.resolve()
          : ecrire(() => source.partager(etiquetteId, corps), 'Le partage a été enregistré et journalisé.', {
              alerte: null,
              destinatairesCoches: [],
            }),
      onOuvrirConsentement: (finalite, accorde) => changer({}, { consentement: { finalite, accorde, canal: null }, message: null }),
      onChoisirCanal: (canal) => {
        const en = etat.vue.consentement;
        if (en) changer({}, { consentement: { ...en, canal } });
      },
      onAnnulerConsentement: () => changer({}, { consentement: null }),
      onConfirmerConsentement: () => {
        const en = etat.vue.consentement;
        // Aucune écriture sans fiche chargée ni canal choisi par l'équipe : c'est la trace de preuve du consentement.
        if (!en || en.canal === null || !etat.donnees || !selection) return Promise.resolve();
        const canal = en.canal;
        return ecrire(
          () => source.enregistrerConsentement(selection.personId, en.finalite, en.accorde, canal),
          'Le choix a été enregistré et daté.',
          {
            consentement: null,
          },
        );
      },
      onPause: (planId) => ecrire(() => source.pauser(planId, MOTIF_PAUSE), 'Les relances sont suspendues.'),
      onReprise: (planId) => ecrire(() => source.reprendre(planId), 'Les relances reprennent.'),
    },
  };
}
